import React, { useMemo, useState } from "react";

import InstanceParameterBindingsInspector from "../definition-editor/InstanceParameterBindingsInspector";
import InstanceParameterOverridesInspector from "../definition-editor/InstanceParameterOverridesInspector";

const HIDDEN_PREFIXES = ["parent_", "global_", "project_"];

function DevTab({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap",
        active
          ? "border-blue-600 text-blue-700 bg-blue-50"
          : "border-transparent text-slate-500 hover:text-slate-800",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function SectionHeader({ title, description }) {
  return (
    <div>
      <div className="font-semibold text-sm text-slate-800">{title}</div>
      {description && (
        <div className="text-xs text-slate-500">{description}</div>
      )}
    </div>
  );
}

function TransformRow({ label, value }) {
  const p = value?.position || {};
  const r = value?.rotation || {};

  return (
    <div className="border rounded bg-white p-2 text-xs">
      <div className="font-semibold text-slate-700 mb-1">{label}</div>
      <div className="grid grid-cols-3 gap-1">
        <div>X: <span className="font-mono">{Number(p.x || 0).toFixed(3)}</span></div>
        <div>Y: <span className="font-mono">{Number(p.y || 0).toFixed(3)}</span></div>
        <div>Z: <span className="font-mono">{Number(p.z || 0).toFixed(3)}</span></div>
      </div>
      <div className="grid grid-cols-3 gap-1 text-slate-500 mt-1">
        <div>RX: <span className="font-mono">{Number(r.x || 0).toFixed(3)}</span></div>
        <div>RY: <span className="font-mono">{Number(r.y || 0).toFixed(3)}</span></div>
        <div>RZ: <span className="font-mono">{Number(r.z || 0).toFixed(3)}</span></div>
      </div>
    </div>
  );
}

function SourceActionButton({ children, onClick, disabled }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="px-3 py-2 rounded border bg-white text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function getResolvedNodeKind(node) {
  const nodeType = String(node?.nodeType || "").toLowerCase();

  if (nodeType === "item_assembly_frame") return "Item assembly frame";
  if (nodeType === "asset_component") return "Asset component";
  if (nodeType === "mounting_item") return "Mounting item";
  if (node?.parentGeneratedFromId || node?.panelKey) return "Generated node";
  if (node?.definitionId) return "Definition instance";

  return "Node";
}

function ResolvedParametersView({ resolvedNode }) {
  const visibleParams = useMemo(() => {
    const params = resolvedNode?.effectiveParameters || {};
    const ownKeys = resolvedNode?.ownParameterKeys || [];

    return Object.entries(params).filter(([key]) => {
      if (HIDDEN_PREFIXES.some((prefix) => key.startsWith(prefix))) {
        return false;
      }

      if (ownKeys.length) {
        return ownKeys.includes(key);
      }

      return true;
    });
  }, [resolvedNode]);

  if (!resolvedNode) {
    return (
      <div className="border rounded p-4 bg-slate-50 text-sm text-slate-500">
        Select an element to inspect resolved parameters.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Resolved parameters"
        description="Final values used by geometry and generated children."
      />

      <div className="border rounded bg-white overflow-hidden">
        <div className="px-3 py-2 bg-slate-50 border-b">
          <div className="font-medium text-sm">
            {resolvedNode.name || resolvedNode.id}
          </div>
          <div className="text-xs text-slate-500">
            {resolvedNode.category || resolvedNode.nodeType || "node"}
          </div>
        </div>

        {visibleParams.length ? (
          <div className="divide-y">
            {visibleParams.map(([key, value]) => (
              <div
                key={key}
                className="flex justify-between gap-3 px-3 py-2 text-sm"
              >
                <span className="text-slate-600 break-all">{key}</span>
                <span className="font-mono text-slate-900 break-all">
                  {String(value)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-3 text-sm text-slate-500">
            No visible resolved parameters.
          </div>
        )}
      </div>
    </div>
  );
}

function ParameterFlowView({ resolvedNode }) {
  const debug = resolvedNode?.parameterDebug;

  if (!debug) {
    return (
      <div className="border rounded p-4 bg-slate-50 text-sm text-slate-500">
        Select an element to inspect parameter flow.
      </div>
    );
  }

  const groups = [
    ["Project / Global", debug.globalParameters],
    ["Parent", debug.parentParameters],
    ["Definition defaults", debug.definitionDefaults],
    ["Instance bindings", debug.parameterBindings],
    ["Instance overrides", debug.parameterOverrides],
    ["Constraints", debug.constraints],
    ["Effective result", debug.effectiveParameters],
  ];

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Parameter flow"
        description="How values are inherited and resolved."
      />

      {groups.map(([title, obj]) => (
        <details
          key={title}
          className="border rounded bg-white"
          open={title === "Effective result"}
        >
          <summary className="px-3 py-2 cursor-pointer text-sm font-medium bg-slate-50">
            {title}
          </summary>

          <div className="divide-y">
            {Object.entries(obj || {}).length ? (
              Object.entries(obj || {}).map(([key, value]) => (
                <div
                  key={key}
                  className="flex justify-between gap-3 px-3 py-2 text-xs"
                >
                  <span className="text-slate-600 break-all">{key}</span>
                  <span className="font-mono text-slate-900 break-all">
                    {String(value)}
                  </span>
                </div>
              ))
            ) : (
              <div className="p-3 text-xs text-slate-500">Empty</div>
            )}
          </div>
        </details>
      ))}
    </div>
  );
}

function SourceTraceView({ resolvedNode, onOpenResolvedSource }) {
  if (!resolvedNode) {
    return (
      <div className="border rounded p-4 bg-slate-50 text-sm text-slate-500">
        Select a node to inspect source links.
      </div>
    );
  }

  const nodeType = String(resolvedNode.nodeType || "").toLowerCase();

  const sourceItemId = resolvedNode.source?.itemId;
  const componentItemId = resolvedNode.component_item?.id;
  const assetId = resolvedNode.asset?.id;

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Source / Transform audit"
        description="Shows where this selected node comes from and where it should be edited."
      />

      <div className="border rounded bg-white p-3 space-y-2 text-sm">
        <div className="flex justify-between gap-3">
          <span className="text-slate-500">Kind</span>
          <span className="font-medium">{getResolvedNodeKind(resolvedNode)}</span>
        </div>

        <div className="flex justify-between gap-3">
          <span className="text-slate-500">Node type</span>
          <span className="font-mono text-xs">{resolvedNode.nodeType || "—"}</span>
        </div>

        <div className="flex justify-between gap-3">
          <span className="text-slate-500">Role</span>
          <span className="font-mono text-xs">{resolvedNode.role || "—"}</span>
        </div>

        <div className="flex justify-between gap-3">
          <span className="text-slate-500">Definition</span>
          <span className="font-mono text-xs">{resolvedNode.definitionId || "—"}</span>
        </div>

        <div className="flex justify-between gap-3">
          <span className="text-slate-500">Source item</span>
          <span className="font-mono text-xs">{sourceItemId || "—"}</span>
        </div>

        <div className="flex justify-between gap-3">
          <span className="text-slate-500">Component item</span>
          <span className="font-mono text-xs">{componentItemId || "—"}</span>
        </div>

        <div className="flex justify-between gap-3">
          <span className="text-slate-500">Asset</span>
          <span className="font-mono text-xs">{assetId || "—"}</span>
        </div>
      </div>

      <div className="border rounded bg-blue-50 border-blue-100 p-3 text-xs text-blue-800">
        {nodeType === "asset_component" ? (
          <>
            This is generated from Item Assembly. Do not edit its transform directly here.
            Use the source editors below.
          </>
        ) : nodeType === "item_assembly_frame" ? (
          <>
            This frame comes from Item Mounting Editor and controls the whole linked item assembly.
          </>
        ) : nodeType === "mounting_item" ? (
          <>
            This mounting item is generated from mounting data. Edit it in its source mounting editor.
          </>
        ) : (
          <>
            Regular scene/definition node.
          </>
        )}
      </div>

      <div className="grid grid-cols-1 gap-2">
        <SourceActionButton
          disabled={!resolvedNode.definitionId}
          onClick={() => onOpenResolvedSource?.(resolvedNode, "definition")}
        >
          Open Definition
        </SourceActionButton>

        <SourceActionButton
          disabled={!sourceItemId}
          onClick={() => onOpenResolvedSource?.(resolvedNode, "assembly")}
        >
          Open Main Item Assembly
        </SourceActionButton>

        <SourceActionButton
          disabled={!componentItemId}
          onClick={() => onOpenResolvedSource?.(resolvedNode, "component-item-mounting")}
        >
          Open Component Item Mounting
        </SourceActionButton>

        <SourceActionButton
          disabled={!assetId}
          onClick={() => onOpenResolvedSource?.(resolvedNode, "asset-mounting")}
        >
          Open Asset Mounting
        </SourceActionButton>
      </div>

      <div className="space-y-2">
        <SectionHeader
          title="Transform stack"
          description="Resolved world transforms by hierarchy level."
        />

        <TransformRow
          label="Parent world"
          value={resolvedNode.parentWorldTransform}
        />

        <TransformRow
          label="Component placement"
          value={resolvedNode.componentPlacementWorldTransform}
        />

        <TransformRow
          label="Component item mounting"
          value={resolvedNode.componentItemMountingWorldTransform}
        />

        <TransformRow
          label="Asset mounting / processing"
          value={resolvedNode.mountingWorldTransform}
        />

        <TransformRow
          label="Final world"
          value={resolvedNode.worldTransform}
        />
      </div>
    </div>
  );
}

function DebugJsonView({ selectedNode, resolvedNode }) {
  return (
    <div className="space-y-3">
      <SectionHeader
        title="Debug JSON"
        description="Raw selected and resolved data."
      />

      <details className="border rounded bg-white" open>
        <summary className="px-3 py-2 cursor-pointer text-sm font-medium bg-slate-50">
          Resolved node
        </summary>

        <pre className="text-xs p-3 overflow-auto max-h-[520px] bg-slate-950 text-slate-100">
          {JSON.stringify(resolvedNode || {}, null, 2)}
        </pre>
      </details>

      <details className="border rounded bg-white">
        <summary className="px-3 py-2 cursor-pointer text-sm font-medium bg-slate-50">
          Scene node
        </summary>

        <pre className="text-xs p-3 overflow-auto max-h-[520px] bg-slate-950 text-slate-100">
          {JSON.stringify(selectedNode || {}, null, 2)}
        </pre>
      </details>
    </div>
  );
}

export default function DeveloperPropertiesPanel({
  selectedNode,
  resolvedNode,
  onChangeNode,
  onOpenResolvedSource,
}) {
  const [tab, setTab] = useState("source");

  const isGenerated =
    Boolean(resolvedNode?.parentGeneratedFromId) ||
    Boolean(resolvedNode?.panelKey) ||
    Boolean(resolvedNode?.overrideKey);

  return (
    <div className="space-y-4">
      {isGenerated && (
        <div className="border rounded p-3 bg-amber-50 text-xs text-amber-800">
          This is generated geometry. Direct data is stored on the parent node
          through partitions, shelf overrides or child definition overrides.
        </div>
      )}

      <div className="border rounded bg-white overflow-hidden">
        <div className="flex gap-1 overflow-x-auto border-b bg-slate-50 px-2">
          <DevTab active={tab === "source"} onClick={() => setTab("source")}>
  Source
</DevTab>

          <DevTab active={tab === "resolved"} onClick={() => setTab("resolved")}>
            Resolved
          </DevTab>

          <DevTab active={tab === "flow"} onClick={() => setTab("flow")}>
            Flow
          </DevTab>

          <DevTab active={tab === "bindings"} onClick={() => setTab("bindings")}>
            Bindings
          </DevTab>

          <DevTab active={tab === "overrides"} onClick={() => setTab("overrides")}>
            Overrides
          </DevTab>

          <DevTab active={tab === "debug"} onClick={() => setTab("debug")}>
            Debug
          </DevTab>
        </div>

        <div className="p-3">
          {tab === "source" && (
  <SourceTraceView
    resolvedNode={resolvedNode}
    onOpenResolvedSource={onOpenResolvedSource}
  />
)}

          {tab === "resolved" && (
            <ResolvedParametersView resolvedNode={resolvedNode} />
          )}

          {tab === "flow" && (
            <ParameterFlowView resolvedNode={resolvedNode} />
          )}

          {tab === "bindings" && (
            <InstanceParameterBindingsInspector
              node={selectedNode}
              onChange={onChangeNode}
            />
          )}

          {tab === "overrides" && (
            <InstanceParameterOverridesInspector
              node={selectedNode}
              resolvedNode={resolvedNode}
              onChange={onChangeNode}
            />
          )}

          {tab === "debug" && (
            <DebugJsonView
              selectedNode={selectedNode}
              resolvedNode={resolvedNode}
            />
          )}
        </div>
      </div>
    </div>
  );
}